import { AppealAddressTypeEnum } from '@carm/_core/appeals/models';
import { AppealContactCategoryEnum } from '@carm/_core/appeals/models/appeals.model';
import * as dayjs from 'dayjs';
import { ISoaAppealLineRequest, ISoaPaymentConfirmationRequest, ISoaRequest, ISoaResponse } from './api/soa-api.model';
import { IGetLineAdjustments, ISoaDraftForm, ISoaLineItem, ISoaPaymentConfirmation, SoaCategory, SoaTypeEnum } from './soa-list.model';

export const mapSoaDraftToRequest = (draft: ISoaDraftForm): ISoaRequest => {

    return {
        appealType: draft.reasonCode.appealCategory,
        reasonCode: draft.reasonCode?.reasonCode,
        tariffClassification: draft.reasonCode?.tradeProgram?.tariffClassification,
        origin: draft.reasonCode?.tradeProgram?.origin,
        valuation: draft.reasonCode?.tradeProgram?.valuation,
        marketing: draft.reasonCode?.tradeProgram?.marking,
        safeguard: draft.reasonCode?.tradeProgram?.safeguard,
        normalValue: draft.appealDetails?.typeOfRequest?.normalValue,
        exportPrice: draft.appealDetails?.typeOfRequest?.exportPrice,
        amountSubsidy: draft.appealDetails?.typeOfRequest?.amountSubsidy,
        amountExportSubsidy: draft.appealDetails?.typeOfRequest?.amountExportSubsidy,
        sameDescription: draft.appealDetails?.typeOfRequest?.sameDescription,
        reasonDescription: draft.appealDetails?.reason,
        goodsDescription: draft.appealDetails?.description,
        statementGroundsDetermination: draft.appealDetails?.grounds,
        statementFactsDetermination: draft.appealDetails?.facts,
        statementEvidence: draft.appealDetails?.evidence,
        extensionReason1: draft.rfe?.withinTimeJustification,
        extensionReason2: draft.rfe?.applicationJustification,
        extensionReason3: draft.rfe?.circumstancesJustification,
        requestForExtension: draft.rfe?.requestForExtension,
        certify: true,
        primaryContactInfo: {
            contactCategory: draft.contactInfo?.contactCategory,
            contactType: draft.contactInfo?.contactType,
            addressType: draft.contactInfo?.contactCategory === AppealContactCategoryEnum.Corporation ?
                                AppealAddressTypeEnum.Coporation : AppealAddressTypeEnum.Individual,
            firstName: draft.contactInfo?.contactCategory === AppealContactCategoryEnum.Individual ?
                                draft.contactInfo.individual?.firstName : draft.contactInfo?.company?.contact,
            lastName: draft.contactInfo?.individual?.lastName,
            line1: draft.contactInfo?.address?.line1,
            line2: draft.contactInfo?.address?.line2,
            town: draft.contactInfo?.address?.town,
            region: draft.contactInfo?.address?.region,
            country: draft.contactInfo?.address?.country,
            postalCode: draft.contactInfo?.address?.postalCode,
            companyName: draft.contactInfo?.company?.name,
            phone: draft.contactInfo?.address?.phone,
            email: draft.contactInfo?.email,
            displayUserName: draft?.contactInfo?.displayUserName,
            certify: draft?.contactInfo?.certify
        },
        appealLines: mapSoaLinesToRequest(draft.selectedLines),
        appealCads: draft.selectedCads,
        ...draft.paymentConfirmation,
    };
};

export const formatLineAdjustments = (data: IGetLineAdjustments): IGetLineAdjustments => {
    const formatDate = (date: string) => dayjs.utc(date).format('YYYY-MM-DD');

    return {
        recordCount: data?.recordCount,
        statementsOfAdjustment: data?.statementsOfAdjustment?.map( line => {
            return {
                ...line,
                id: generateSoaLineId(line?.transactionNumber, line?.lineNumber?.toString(), line?.version),
                dateIssued: formatDate(line?.dateIssued),
            };
        })
    };
};

export const mapSoaLinesToRequest = (lines: ISoaLineItem[]): ISoaAppealLineRequest[] => {
    return lines?.map( l => ({
        transactionNumber: l.transactionNumber,
        version: l.versionNumber,
        lineNumber: l.lineNumber?.toString(),
        guid: l.guid,
        headerGuid: l.headerGuid,
        simaType: l.simaType,
        id: l.id,
        dateIssued: l.dateIssued,
        submittedBy: l.submittedBy,
        reasonCode1: l.reasonCode1,
        reasonCode2: l.reasonCode2,
        reasonCode3: l.reasonCode3,
        classificationNumber: l.classificationNumber,
        countryOfOrigin: l.countryOfOrigin,
        placeOfExport: l.placeExport,
        tariffTreatment: l.tariffTreatment,
        caseNumber: l.caseNumber,
        amount: l.amount
    }));
};

export const generateSoaLineId = (transactionNumber: string, lineNumber: string, version: string): string => {
    return `${transactionNumber}_${lineNumber}_${version}`;
};

export const mapSoaPaymentConfirmationToRequest = (paymentConfirmation: ISoaPaymentConfirmation): ISoaPaymentConfirmationRequest => {
    return {
        payments: paymentConfirmation?.paymentNumbers?.map( p => ( { paymentConfirmationNumber: p } )),
        bonds: paymentConfirmation?.bondNumbers.map( b => ( { bondNumber: b } ))
    }
}

export const mapSoaLinesRequestToForm = (lines: ISoaAppealLineRequest[]): ISoaLineItem[] => {
    return lines?.map( l => ({
        transactionNumber: l.transactionNumber,
        versionNumber: l.version,
        lineNumber: +l.lineNumber,
        guid: l.guid,
        headerGuid: l.headerGuid,
        simaType: l.simaType,
        dateIssued: l.dateIssued,
        submittedBy: l.submittedBy,
        reasonCode1: l.reasonCode1,
        reasonCode2: l.reasonCode2,
        reasonCode3: l.reasonCode3,
        classificationNumber: l.classificationNumber,
        countryOfOrigin: l.countryOfOrigin,
        placeExport: l.placeOfExport,
        tariffTreatment: l.tariffTreatment,
        caseNumber: l.caseNumber,
    }));
};

export const mapSoaRequestToForm = (request: ISoaResponse): ISoaDraftForm => {
    return {
        paymentConfirmation: {
            paymentType: request.paymentType,
            paymentNumbers: request.paymentNumbers,
            bondNumbers: request.bondNumbers,
        },
        reasonCode: {
            appealCategory: request.appealType as SoaCategory,
            reasonCode: request.reasonCode,
            tradeProgram: {
                tariffClassification: request?.tariffClassification,
                origin: request.origin,
                valuation: request.valuation,
                marking: request.marketing,
                safeguard: request.safeguard
            }
        },
        selectedLines: mapSoaLinesRequestToForm(request.appealLines),
        selectedCads: request.appealCads,
        appealDetails: {
            typeOfRequest: {
                normalValue: request.normalValue,
                exportPrice: request.exportPrice,
                amountSubsidy: request.amountSubsidy,
                amountExportSubsidy: request.amountExportSubsidy,
                sameDescription: request.sameDescription
            },
            grounds: request.statementGroundsDetermination,
            facts: request.statementFactsDetermination,
            evidence: request.statementEvidence,
            reason: request.reasonDescription,
            description: request.goodsDescription
        },
        rfe: {
            withinTimeJustification: request.extensionReason1,
            applicationJustification: request.extensionReason2,
            circumstancesJustification: request.extensionReason3,
            requestForExtension: request.requestForExtension
        },
        contactInfo: {
            contactCategory: request.primaryContactInfo?.contactCategory,
            contactType: request.primaryContactInfo?.contactType,
            individual: {
                firstName: request.primaryContactInfo?.firstName,
                lastName: request.primaryContactInfo?.lastName
            },
            company: {
                contact: request.primaryContactInfo?.firstName,
                name: request.primaryContactInfo?.companyName
            },
            address: {
                line1: request.primaryContactInfo?.line1,
                line2: request.primaryContactInfo?.line2,
                town: request.primaryContactInfo?.town,
                region: request.primaryContactInfo?.region,
                country: request.primaryContactInfo?.country,
                postalCode: request.primaryContactInfo?.postalCode,
                phone: request.primaryContactInfo?.phone,
            },
            email: request.primaryContactInfo?.email,
            registerForEmail: request?.primaryContactInfo?.email?.trim() !== '' && request?.primaryContactInfo?.email ? 'yes' : 'no',
            displayUserName: request?.displayUserName,
            certify: request?.certify
        }
    };


}

export const findSoaTypeBySoaCategory = (soaCategory: string): SoaTypeEnum => {
    if (soaCategory === SoaCategory.PreCarm_Sima || soaCategory === SoaCategory.PreCarm_Trade) {
        return SoaTypeEnum.PreCarm;
    } else if (soaCategory === SoaCategory.Sima_Redetermination) {
        return SoaTypeEnum.Sima;
    } else {
        return SoaTypeEnum.StatementOfAdjustment;
    }
}
